import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, X } from 'lucide-react';

interface DeleteDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string;
  onDeleteDocument: () => void | Promise<void>;
  onDeleteDocumentAndOrders: () => void | Promise<void>;
}

export const DeleteDocumentDialog: React.FC<DeleteDocumentDialogProps> = ({
  open,
  onOpenChange,
  title,
  message,
  onDeleteDocument,
  onDeleteDocumentAndOrders,
}) => {
  const [loading, setLoading] = React.useState<'doc' | 'both' | null>(null);

  const run = async (kind: 'doc' | 'both', fn: () => void | Promise<void>) => {
    setLoading(kind);
    try {
      await fn();
      onOpenChange(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-50">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100">
                <AlertTriangle size={24} className="text-red-600" />
              </div>
              <Dialog.Title className="text-xl font-black text-gray-900">
                {title}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="text-gray-600 text-sm mb-2">
            {message}
          </Dialog.Description>
          <p className="text-xs font-bold text-red-700 mb-6">
            This cannot be undone.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => void run('doc', onDeleteDocument)}
              className="min-h-[48px] px-4 bg-orange-600 text-white rounded-lg font-bold disabled:opacity-50"
            >
              {loading === 'doc' ? 'Deleting...' : 'Delete document only'}
            </button>
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => void run('both', onDeleteDocumentAndOrders)}
              className="min-h-[48px] px-4 bg-red-600 text-white rounded-lg font-bold disabled:opacity-50"
            >
              {loading === 'both' ? 'Deleting...' : 'Delete document and orders'}
            </button>
            <button
              type="button"
              disabled={loading !== null}
              onClick={() => onOpenChange(false)}
              className="min-h-[48px] px-4 bg-gray-200 text-gray-700 rounded-lg font-bold"
            >
              Cancel
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
